import { getStoryblokApi, type SbBlokData } from "@storyblok/react/rsc";

import type { RichTextValue } from "@/components/rich-text";

import { storyblokCache } from "./storyblok-cache";
import type { StoryblokLink } from "./storyblok-link";

type StoryblokAsset = { filename?: string; alt?: string };

type ClinicServiceBlok = { _uid?: string; name?: string; link?: StoryblokLink };

export type ClinicContent = {
  title?: string;
  region?: string;
  country?: string;
  flagship?: boolean;
  tagline?: string;
  address_line1?: string;
  address_line2?: string;
  phone?: string;
  email?: string;
  latitude?: number | string;
  longitude?: number | string;
  photo?: StoryblokAsset;
  services?: ClinicServiceBlok[];
  services_image?: StoryblokAsset;
  summary?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  about?: RichTextValue;
  stats?: Array<{ number?: string; label?: string }>;
  opening_hours?: string;
  parking_car?: string;
  parking_transport?: string;
  parking_accessibility?: string;
  body?: SbBlokData[];
};

export type ClinicStory = {
  uuid: string;
  name: string;
  slug: string;
  full_slug: string;
  content: ClinicContent;
};

export type Clinic = {
  uuid: string;
  fullSlug: string;
  name: string;
  region: string;
  country: string;
  flagship: boolean;
  tagline: string;
  addressLine1: string;
  addressLine2: string;
  phone: string;
  email: string;
  lat: number | null;
  lng: number | null;
  photo: { filename: string; alt: string } | null;
  summary: string;
};

export type ServiceLink = { name: string; href: string | null; target?: string };

function num(value: unknown): number | null {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(n) ? n : null;
}

export function toClinic(story: ClinicStory): Clinic {
  const c = story.content || {};
  return {
    uuid: story.uuid,
    fullSlug: story.full_slug,
    name: story.name,
    region: c.region || "",
    country: c.country || "Ireland",
    flagship: Boolean(c.flagship),
    tagline: c.tagline || "",
    addressLine1: c.address_line1 || "",
    addressLine2: c.address_line2 || "",
    phone: c.phone || "",
    email: c.email || "",
    lat: num(c.latitude),
    lng: num(c.longitude),
    photo: c.photo?.filename ? { filename: c.photo.filename, alt: c.photo.alt || "" } : null,
    summary: c.summary || "",
  };
}

// A clinic belongs to one region; its URL follows the folder it lives in, not
// the page being viewed.
export function clinicUrl(clinic: Clinic): string {
  const path = clinic.fullSlug.replace(/^(ie|uk)\//, "");
  const prefix = clinic.fullSlug.startsWith("uk/") ? "/uk" : "";
  return `${prefix}/${path}`;
}

function isUkCountry(country: string | undefined): boolean {
  return (country || "").toLowerCase().includes("kingdom");
}

// Returns clinics in editor (drag) order, not alphabetical. Reads both region
// folders and keeps the story whose folder matches its country, so the locator
// shows every clinic once and each links to its single canonical URL.
export async function fetchClinics(isDraft = false): Promise<Clinic[]> {
  const api = getStoryblokApi();
  const all: ClinicStory[] = [];
  try {
    for (const prefix of ["ie/clinics/", "uk/clinics/"]) {
      let page = 1;
      while (true) {
        const { data } = await api.get(
          "cdn/stories",
          {
            starts_with: prefix,
            filter_query: { component: { in: "clinic" } },
            sort_by: "position:asc",
            per_page: 100,
            page,
            version: isDraft ? "draft" : "published",
          },
          storyblokCache(isDraft, ["clinics"]),
        );
        const stories = (data?.stories || []) as ClinicStory[];
        all.push(...stories);
        if (stories.length < 100) break;
        page++;
        if (page > 5) break;
      }
    }
  } catch {
    return [];
  }
  const byKey = new Map<string, ClinicStory>();
  for (const story of all) {
    const key = story.full_slug.replace(/^(ie|uk)\//, "");
    const folderMatchesCountry =
      story.full_slug.startsWith("uk/") === isUkCountry(story.content?.country);
    if (!byKey.has(key) || folderMatchesCountry) byKey.set(key, story);
  }
  return [...byKey.values()].map(toClinic);
}
